import { z } from 'zod';

import { countryCodeSchema } from '@apple/utils/globalization/models';

import type { BaseOrderSearchListing } from '@apple/features/order/models/search';
import { baseOrderSearchListingSchema } from '@apple/features/order/models/search';
import type { OrderCommentStatus } from '@apple/features/order/models/status';
import { orderCommentStatusSchema } from '@apple/features/order/models/status';
import { programIdSchema } from '@apple/features/program/models';
import { projectIdSchema } from '@apple/features/project/models';

export type OrderSearchFilters = z.infer<typeof orderFiltersSchema>;
export type OrderSearchField = z.infer<typeof orderSearchFieldIdSchema>;
// export type OrderFiltersForm = Prettify<z.infer<typeof orderFiltersFormSchema>>;
export type DateRangeFilter = DateDefinedRangeFilter | DateCustomRangeFilter;
interface DateDefinedRangeFilter {
	range: '30d' | '60d' | '90d';
}

interface DateCustomRangeFilter {
	startDate: Date;
	range: 'custom';
	endDate?: Date | undefined;
}

/** AppleBrandedPrograms.Web.Features.Orders.AppleBrandedOrderSearchController.OrderListing */
export interface OrderSearchListing extends BaseOrderSearchListing {
	appleId: string;
	customerReferenceNumber: string | null;
	displayName: string | null;
	recipient: string;
	attentionTo?: string | null;
	phoneNumber: string;
	isGhostShipment: boolean;
	hasComments?: boolean;
	orderCommentStatus?: OrderCommentStatus;
}

export interface AllowedOrderSearchFilters {
	filters: {
		id: OrderSearchField;
		name: string;
	}[];
}

/** @see AppleBrandedPrograms.Web.Features.Orders.OrderListing */
export const orderSearchListingSchema = baseOrderSearchListingSchema.extend({
	appleId: z.string(),
	customerReferenceNumber: z.string().nullable(),
	displayName: z.string().nullable(),
	recipient: z.string(),
	attentionTo: z.string().optional().nullable(),
	phoneNumber: z.string(),
	isGhostShipment: z.boolean(),
	hasComments: z.boolean().optional(),
	orderCommentStatus: orderCommentStatusSchema.optional(),
}) satisfies z.ZodType<OrderSearchListing>;

/** @see AppleBrandedPrograms.Web.Features.Orders.OrderFilter */
export const orderFiltersSchema = z
	.object({
		orderNumber: z.string(),
		orderStatus: z.string(),
		warehouseCode: z.string(),
		countryCode: countryCodeSchema,
		customerGroupId: programIdSchema,
		orderDateRangeInDays: z.number().or(z.literal('custom')),
		startDate: z.coerce.date().or(z.string()),
		endDate: z.coerce.date(),
		userId: z.string(),
		appleId: z.string(),
		partNumber: z.string(),
		projectId: projectIdSchema,
		searchTerm: z.string(),
		reason: z.string(),
	})
	.partial();

const definedDateRangeFilterSchema = z.object({
	range: z.enum(['30d', '60d', '90d']),
}) satisfies z.ZodType<DateDefinedRangeFilter>;

const customDateRangeFilterSchema = z.object({
	range: z.literal('custom'),
	startDate: z.date(),
	endDate: z.date().optional(),
}) satisfies z.ZodType<DateCustomRangeFilter>;

export const dateRangeFilterSchema = z.discriminatedUnion('range', [
	definedDateRangeFilterSchema,
	customDateRangeFilterSchema,
]) as z.ZodType<DateRangeFilter>; // 	.catch({ range: '30d' });

// export const orderSearchResponseSchema = createODataPageResultSchema(orderSearchListingSchema);

// export const orderFiltersFormSchema = orderFiltersSchema.extend({
// 	dateRange: dateRangeFilterSchema.catch('60'),
// 	// startDate: z.string().optional(),
// 	// endDate: z.string().optional(),
// });

// Route's url search params
// export const orderSearchParamsSchema = orderFiltersSchema.extend({
// 	page: z.number().catch(1),
// 	limit: z.number().catch(25),
// 	startDate: z
// 		.string()
// 		.optional()
// 		.transform(x => (x === undefined ? undefined : dayjs(x).toDate())),
// 	endDate: z
// 		.string()
// 		.optional()
// 		.transform(x => (x === undefined ? undefined : dayjs(x).toDate())),
// });

const orderSearchFieldIdSchema = z.enum([
	'AppleId',
	'OrderStatus',
	'PartNumber',
	'UserId',
	'Program',
	'Country',
	'Plant',
	'Project',
	'Reason',
]);

export const allowedOrderSearchFiltersSchema = z.object({
	filters: z.array(
		z.object({
			id: orderSearchFieldIdSchema,
			name: z.string(),
		}),
	),
}) satisfies z.ZodType<AllowedOrderSearchFilters>;
